/* DP24 helpers (loaded deferred on every page; each part checks its own page).

   Photo kit: as long as no photo is uploaded, the "Add to cart" buttons (the main one on the
   page and the sticky bar) do NOT start Horizon's add-to-cart (no flying picture, no cart
   bubble, no drawer). Instead the customer is taken to the upload box, it is highlighted and
   a short hint appears under the button. With a photo, everything works as usual.
   (2026-10-01: before, the main button let Horizon's "added" animation run while the upload
   app only showed an error popup – it looked as if the kit was in the cart.)
   Fail-safe: if our photo check is ever wrong, the third click goes through
   to the upload app, which then validates on its own. */
(() => {
  const findZone = () => {
    const guide = document.querySelector('.dp24-upload-guide');
    if (!guide) return null;
    const next = guide.nextElementSibling;
    return next && next.classList.contains('shopify-app-block') ? next : guide;
  };

  const hasPhoto = (zone) => {
    /* 2026-10-01: shared upload state from blocks/dp24-upload-guide (whole section, survives re-renders) */
    if (window.DP24Photo) return window.DP24Photo.has();
    const files = [...zone.querySelectorAll('input[type="file"]')].some((i) => i.files && i.files.length > 0);
    const stored = [...document.querySelectorAll('product-form-component input[name^="properties"], form[action*="/cart/add"] input[name^="properties"]')].some(
      (i) => i.type !== 'file' && i.value && /^https?:\/\/|\.(jpe?g|png|heic|heif|webp|gif)(\?|$)/i.test(i.value)
    );
    const preview = [...zone.querySelectorAll('img')].some((img) => {
      const src = img.currentSrc || img.src || '';
      return /^blob:|^data:image|uploadery|\/uploads?\//i.test(src);
    });
    return files || stored || preview;
  };

  const BUTTONS = '.sticky-add-to-cart__button, product-form-component [name="add"], .product-form-buttons [name="add"]';
  let warnedAt = 0;

  const showHint = (button) => {
    const host = button.closest('.sticky-add-to-cart__bar') ? null : button.parentElement;
    if (!host) return;
    let hint = host.querySelector('.dp24-upload-hint');
    if (!hint) {
      hint = document.createElement('p');
      hint.className = 'dp24-upload-hint';
      hint.setAttribute('role', 'alert');
      host.appendChild(hint);
    }
    hint.textContent = 'Please upload your photo first – then you can add your kit to the cart.';
    hint.hidden = false;
    clearTimeout(hint._t);
    hint._t = setTimeout(() => (hint.hidden = true), 8000);
  };

  window.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest(BUTTONS) : null;
      if (!button || button.disabled) return;
      const zone = findZone();
      if (!zone || hasPhoto(zone)) return;
      if (warnedAt >= 2) return; // fail-safe: third click goes through to the upload app
      warnedAt += 1;
      event.preventDefault();
      event.stopImmediatePropagation();
      showHint(button);
      const target = document.querySelector('.dp24-upload-cta') || zone;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('dp24-attention');
      setTimeout(() => target.classList.remove('dp24-attention'), 2400);
      const focusable = target.querySelector('.dp24-upload-cta__btn, input[type="file"], button, [tabindex]');
      focusable?.focus({ preventScroll: true });
    },
    true
  );

  /* Hint disappears as soon as a photo is chosen */
  const photoChosen = () => {
    document.querySelectorAll('.dp24-upload-hint').forEach((h) => (h.hidden = true));
    warnedAt = 0;
  };
  document.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'file') return;
    photoChosen();
  });
  document.addEventListener('dp24:photo-change', (event) => {
    if (event.detail?.ready) photoChosen();
  });
})();

/* Sizes in the sticky "Add to cart" bar: Horizon prints the raw variant title
   ("20x25 cm (7.87x9.84 inch) / Square / No Frame"). Show the same words as the "Your kit"
   summary of the option cards ("8 × 10 in (20 × 25 cm) · Square diamonds · Rolled canvas").
   Without the summary: "8 × 10 in (20 × 25 cm) · Square · No Frame". (2026-10-01, summary 2026-10-02) */
(() => {
  const CM = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*cm\b/gi;
  const INCH = /\s*\(\s*\d+(?:[.,]\d+)?\s*[x×]\s*\d+(?:[.,]\d+)?\s*inch(?:es)?\s*\)/gi;
  const toIn = (n) => Math.round(parseFloat(String(n).replace(',', '.')) / 2.54);
  const convert = (text) => {
    if (text.indexOf(' in (') !== -1) return text;
    return text
      .replace(INCH, '')
      .replace(CM, (m, a, b) => `${toIn(a)} × ${toIn(b)} in (${a} × ${b} cm)`)
      .replace(/\s+\/\s+/g, ' · ');
  };
  const summary = () => document.querySelector('[data-vo-sum-text]');

  const fix = (el) => {
    const sum = summary();
    const sumText = sum ? sum.textContent.trim() : '';
    const next = sumText || convert(el.textContent);
    if (next !== el.textContent) el.textContent = next;
  };

  const start = () => {
    const els = document.querySelectorAll('.sticky-add-to-cart__variant');
    if (!els.length) return;
    const fixAll = () => els.forEach(fix);
    fixAll();
    els.forEach((el) => new MutationObserver(() => fix(el)).observe(el, { childList: true, characterData: true, subtree: true }));
    const sum = summary();
    if (sum) new MutationObserver(fixAll).observe(sum, { childList: true, characterData: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* Product page: the stars under the title ("★★★★★ 16 reviews") jump to the customer reviews,
   as shoppers expect. The reviews are the Loox section of the product template. (2026-10-02) */
(() => {
  const target = () => document.querySelector('[id$="__dp24_reviews"]') || document.getElementById('looxReviews');

  const start = () => {
    const rating = document.querySelector('.product-details .rating-wrapper');
    if (!rating || !target()) return;
    const count = rating.querySelector('.rating-count');
    rating.classList.add('dp24-rating-link');
    rating.setAttribute('role', 'link');
    rating.setAttribute('tabindex', '0');
    if (count) rating.setAttribute('aria-label', `${rating.querySelector('.rating')?.getAttribute('aria-label') || ''} – read ${count.textContent.trim()}`.replace(/^ – /, ''));
    const go = () => target()?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    rating.addEventListener('click', go);
    rating.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        go();
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* Empty cart (drawer + cart page): the "Keep shopping" button goes to the bestsellers, not to
   /collections/all. Horizon renders that button from snippets/cart-products (theme setting
   "empty cart button link", default = all products); the cart re-renders often, so the link is
   corrected on every render and – as a safety net – on click. (2026-10-01) */
(() => {
  const TARGET = `${window.Shopify?.routes?.root || '/'}collections/bestseller`;
  const isAll = (a) => {
    try {
      return /\/collections\/all\/?$/.test(new URL(a.getAttribute('href') || '', location.href).pathname);
    } catch {
      return false;
    }
  };
  const fix = () => {
    document.querySelectorAll('a.cart-items__empty-button').forEach((a) => {
      if (isAll(a)) a.setAttribute('href', TARGET);
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const a = event.target instanceof Element ? event.target.closest('a.cart-items__empty-button') : null;
      if (a && isAll(a)) a.setAttribute('href', TARGET);
    },
    true
  );

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      fix();
    });
  };
  const start = () => {
    fix();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
